/* eslint-disable class-methods-use-this */
import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import LayoutCard from 'components/FrictionlessCheckout/LayoutCard/LayoutCard';
import AccessPoint from 'components/FrictionlessCheckout/DeliverTo/AccessPoint';
import AddressDisplay from 'components/FrictionlessCheckout/DeliverTo/AddressDisplay';
import ShipToAccessPoint from 'components/FrictionlessCheckout/DeliverTo/ShipToAccessPoint';
import SDDInstructions from 'components/FrictionlessCheckout/DeliverTo/SDDInstructions';
import FrictionlessCheckoutConstants from 'utils/FrictionlessCheckoutConstants';
import ShipAddressSection from 'components/FrictionlessCheckout/DeliverTo/ShipAddress/Section';
import { Box } from 'components/ui';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import checkoutUtils from 'utils/Checkout';
import anaConsts from 'analytics/constants';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import Empty from 'constants/empty';
import AddressActions from 'actions/AddressActions';
import addressUtils from 'utils/Address';
import orderUtils from 'utils/Order';
import store from 'store/Store';

import { SECTION_NAMES, FC_CHECKOUT_SECTIONS, FC_CHECKOUT_PATH } from 'constants/frictionlessCheckout';

const {
    ORDER_TYPES: { GET_IT_SHIPPED }
} = FrictionlessCheckoutConstants;

const HAL_ADDRESS = 'HAL';

const ShippingAddressInformation = ({
    isHal,
    address,
    halOperatingHours,
    isHalAvailable,
    children,
    setIsEdit,
    holdAtLocation,
    showAccessPoint = true,
    showDisplayAddress = true
}) => (
    <Box paddingX={[4, 4, 5]}>
        {showDisplayAddress && (
            <AddressDisplay
                address={address}
                isHal={isHal}
                halOperatingHours={halOperatingHours}
                holdAtLocation={holdAtLocation}
            >
                {isHalAvailable && showAccessPoint && (
                    <AccessPoint
                        isHal={isHal}
                        accessPointCallback={setIsEdit}
                    />
                )}
            </AddressDisplay>
        )}
        {children}
    </Box>
);

class DeliverTo extends BaseClass {
    state = {
        isHal: false,
        isEditByChangeLink: false,
        isSddWithoutAddressMatch: false,
        loadingAddress: this.props.isSdd,
        isNewAddressAdded: false
    };

    getSubtitle = () => {
        const { localization, isSdd, isAutoReplenishBasket, isGis } = this.props;
        const { sDDAndGISOrders, gISAndAROrders, sDDAndAROrders, sDDAndGISAndAROrders } = localization;
        let subtitle = null;

        if (isSdd && isGis && isAutoReplenishBasket) {
            subtitle = sDDAndGISAndAROrders;
        } else if (isGis && isAutoReplenishBasket) {
            subtitle = gISAndAROrders;
        } else if (isSdd && isAutoReplenishBasket) {
            subtitle = sDDAndAROrders;
        } else if (isSdd && isGis) {
            subtitle = sDDAndGISOrders;
        }

        return subtitle;
    };

    handlePayPalUnverified = () => this.setEditChangeLink(true, false);

    /**
     * Handle recommended address callback from address validation
     * Updates the selected PayPal address with the recommended address
     */
    handlePayPalRecommended = recommendedAddress => {
        const { shippingGroup, updateShippingAddress, refreshCheckout } = this.props;
        const currentPayPalAddress = shippingGroup?.address;

        if (!currentPayPalAddress || !shippingGroup?.shippingGroupId) {
            return;
        }

        // Extract fields that shouldn't be sent to API, and create sanitized copy
        const { isPOBoxAddress: recommendedIsPOBoxAddress, addressType: recommendedAddressType, ...sanitizedRecommendedAddress } = recommendedAddress;

        // Merge current PayPal address with recommended address.
        // The backend persists the shipping email under `shipEmail`, not `email`, so
        // without explicitly mapping it here the PayPal-provided email is dropped on update and
        // submitOrder later fails with `checkout.shipping.email.invalid`.
        const updatedAddress = Object.assign(
            {},
            currentPayPalAddress,
            sanitizedRecommendedAddress,
            currentPayPalAddress?.email ? { shipEmail: currentPayPalAddress.email } : {}
        );

        // Prepare data for updateShippingAddress API
        const dataToSend = {
            address: updatedAddress,
            isPOBoxAddress: recommendedIsPOBoxAddress,
            addressType: recommendedAddressType || 'PAYPAL',
            isDefaultAddress: false, // PayPal addresses are typically not default
            addressValidated: true,
            shippingGroupId: shippingGroup.shippingGroupId
        };

        // Update shipping address with recommended address
        updateShippingAddress(dataToSend)
            .then(() => {
                // Refresh checkout to update order details in the store
                return refreshCheckout()();
            })
            .catch(error => {
                Sephora.logger.error('[PayPal Address Validation] Failed to update shipping address:', error);
            });
    };

    noop = () => {};

    /**
     * Validate PayPal address with SDD-specific logic
     * For SDD orders: Validates selected address only if it's a PayPal address
     * and preferredZipCode matches the selected address postalCode
     */
    validatePayPalAddress = () => {
        const { orderDetails, addressList, isSdd, shippingGroup } = this.props;

        // Guard: Don't validate if orderDetails not available
        if (!orderDetails) {
            return;
        }

        // Get the selected address from shippingGroup
        const selectedAddress = shippingGroup?.address;

        // Guard: Only validate PayPal addresses
        if (!selectedAddress || !orderUtils.isPayPalAddress(selectedAddress)) {
            return;
        }

        // For SDD, only validate when the preferred ZIP matches the selected PayPal address ZIP
        if (isSdd) {
            const preferredZipCode = store.getState().user?.preferredZipCode;
            const selectedPostalCode = selectedAddress.postalCode;

            if (!addressUtils.zipCodesMatch(preferredZipCode, selectedPostalCode)) {
                return;
            }
        }

        // Note: Manual testing has confirmed this validation only triggers once per address
        FrictionlessUtils.validatePayPalAddress({
            orderDetails,
            addressList,
            onUnverified: this.handlePayPalUnverified,
            onRecommended: this.handlePayPalRecommended,
            onCancel: this.noop,
            validateAddressFn: AddressActions.validateAddress,
            pageType: anaConsts.PAGE_TYPES.CHECKOUT
        });
    };

    componentDidMount() {
        this.props.getAddressBook(false).then(data => {
            this.setState({ loadingAddress: false });

            // Validate PayPal address after address book is loaded
            this.validatePayPalAddress();

            if (!this.props.skipAutoRouting) {
                FrictionlessUtils.updateCheckoutURLBasedOnOrderCompleteness(data?.addressList);
            }
        });

        this.setState({ isHal: this.props.shippingGroup.address?.addressType === HAL_ADDRESS });
    }

    componentDidUpdate(prevProps) {
        const { shippingGroup: prevShippingGroup, addressByPreferredZipCode: prevAddressByPreferredZipCode } = prevProps;
        const { shippingGroup, addressByPreferredZipCode, isSdd, isEditViewFromURL } = this.props;

        // Validate PayPal address when selected address becomes available or changes
        if (shippingGroup?.address && prevShippingGroup?.address?.addressId !== shippingGroup.address.addressId) {
            this.validatePayPalAddress();
        }

        if (prevShippingGroup.address?.addressType !== shippingGroup.address?.addressType) {
            this.setState({ isHal: shippingGroup.address?.addressType === HAL_ADDRESS });
        }

        if (prevAddressByPreferredZipCode !== addressByPreferredZipCode && addressByPreferredZipCode > 1) {
            this.setEditChangeLink(true, false);
        }

        if (
            isSdd &&
            !this.state.isSddWithoutAddressMatch &&
            !this.state.loadingAddress &&
            addressByPreferredZipCode === 0 &&
            !this.state.isNewAddressAdded
        ) {
            this.setState({ isSddWithoutAddressMatch: true });
        }

        if (!isEditViewFromURL && isEditViewFromURL !== prevProps.isEditViewFromURL) {
            this.state.isEditByChangeLink &&
                this.setState({
                    isEditByChangeLink: false
                });

            if (this.state.isAccessPointFlow && !this.props.shippingGroup.address.addressId) {
                this.setState({
                    isAccessPointFlow: false
                });
                // Access Point flow creates a draft HAL address (CREATE_DRAFT_HAL_ADDRESS),
                // which removes the saved shipping group from store. On back mid-flow, discard the draft and reload order details.
                this.props.refreshCheckout()();
            }
        }
    }

    setIsEdit = isEdit => {
        isEdit && this.props.goToURL({ path: `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.DELIVER_TO.path}` });
        this.setState({
            isAccessPointFlow: isEdit
        });
    };

    setEditChangeLink = (isEditView, isTriggeredByUserClick = false) => {
        this.setState({ isEditByChangeLink: isEditView });

        if (!isEditView) {
            // Notify parent that address section was explicitly closed (saved/cancelled)
            // This clears forced edit mode set for initially unverified addresses
            this.props.onAddressSaved?.();
        }

        if (isEditView) {
            const isGuestCheckout = checkoutUtils.isGuestOrder();
            isGuestCheckout && this.props.setAddressNeedsConfirmation(true);
            this.fireAnalytics(isTriggeredByUserClick);
            this.props.setActiveSection(this.props.sectionName);
            this.props.goToURL({ path: `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.DELIVER_TO.path}` });
        }
    };

    fireAnalytics = isTriggeredByUserClick => {
        const isGuestCheckout = checkoutUtils.isGuestOrder();
        !isGuestCheckout && FrictionlessCheckoutBindings.setChangeLinkAnalytics(anaConsts.PAGE_TYPES.SHIPPING, isTriggeredByUserClick);
    };

    setHalWhenChangeLink = isEditView => {
        this.setState({ isEditByChangeLink: !isEditView, isAccessPointFlow: isEditView });
    };

    setIsSddWithoutAddressMatch = isSddWithoutAddressMatch => {
        this.setState({ isSddWithoutAddressMatch, isNewAddressAdded: true });
    };

    resetAccessPoint = () => {
        this.setState({
            isAccessPointFlow: false
        });
    };

    // eslint-disable-next-line complexity
    renderCardBody = () => {
        const {
            localization,
            isZeroCheckout,
            addressList,
            isHalAvailable,
            isNewUserFlow,
            halOperatingHours,
            shippingGroup,
            orderHasPhysicalGiftCard,
            isSdd,
            isCollapsed,
            isEditViewFromURL
        } = this.props;

        const { address, isComplete, shippingGroupId, shippingMethod } = shippingGroup;

        const { isHal, isEditByChangeLink, isSddWithoutAddressMatch } = this.state;
        const isGuestCheckout = checkoutUtils.isGuestOrder();
        const showCancelButtonInChild = addressList.length > 0;

        return (
            <>
                {(isEditByChangeLink || isEditViewFromURL) &&
                    Object.keys(address || Empty.Object).length > 0 &&
                    isComplete &&
                    !this.state.isAccessPointFlow &&
                    !(isSdd && isSddWithoutAddressMatch) && (
                    <Box
                        paddingX={[4, 4, 5]}
                        minHeight={133}
                    >
                        <ShipAddressSection
                            isNewUserFlow={isNewUserFlow}
                            shippingAddress={address}
                            profileAddresses={addressList || []}
                            shippingGroupId={shippingGroupId}
                            isComplete={isComplete}
                            orderHasPhysicalGiftCard={orderHasPhysicalGiftCard}
                            isZeroCheckout={isZeroCheckout}
                            isGuestCheckout={isGuestCheckout}
                            shippingMethod={shippingMethod}
                            setAccessPoint={this.setEditChangeLink}
                            isHalAvailable={isHalAvailable}
                            isSdd={isSdd}
                            isCollapsed={isCollapsed}
                            onAddressSelect={this.setSelectedAddressId}
                            setIsAddingNewAddress={this.setIsAddingNewAddress}
                            isEditView={isEditViewFromURL}
                            showTaxExemptAddress
                            showCancelButtonInChild={showCancelButtonInChild}
                            onAddressSaved={this.props.onAddressSaved}
                        >
                            {isHalAvailable && (
                                <AccessPoint
                                    isHal={isHal}
                                    accessPointCallback={this.setHalWhenChangeLink}
                                    full
                                />
                            )}
                        </ShipAddressSection>
                    </Box>
                )}
                {!isSddWithoutAddressMatch && address && isComplete ? (
                    <ShippingAddressInformation
                        isHal={isHal}
                        address={address}
                        halOperatingHours={halOperatingHours}
                        isHalAvailable={isHalAvailable}
                        setIsEdit={this.setIsEdit}
                        showAccessPoint={isEditViewFromURL || (!isEditViewFromURL && !isHal)}
                        holdAtLocation={localization.holdAtLocation}
                        showDisplayAddress={(!isEditByChangeLink && !isEditViewFromURL) || !!this.state.isAccessPointFlow}
                    >
                        {isHal && !isEditByChangeLink && (
                            <ShipToAccessPoint
                                isComplete={isComplete}
                                shippingAddress={address}
                                editMode={isEditViewFromURL}
                                shippingGroupId={shippingGroupId}
                                callback={this.setIsEdit}
                                isGuestCheckout={isGuestCheckout}
                                shipToAddressCallback={this.resetAccessPoint}
                                onAddressSaved={this.props.onAddressSaved}
                            />
                        )}
                    </ShippingAddressInformation>
                ) : (
                    <Box paddingX={[4, 4, 5]}>
                        {isHal ? (
                            <ShippingAddressInformation
                                isHal={isHal}
                                address={address}
                                halOperatingHours={halOperatingHours}
                                isHalAvailable={isHalAvailable}
                                setIsEdit={this.setIsEdit}
                                holdAtLocation={localization.holdAtLocation}
                            >
                                <ShipToAccessPoint
                                    isComplete={isComplete}
                                    shippingAddress={address}
                                    editMode={isEditViewFromURL}
                                    shippingGroupId={shippingGroupId}
                                    callback={this.setIsEdit}
                                    isGuestCheckout={isGuestCheckout}
                                    shipToAddressCallback={this.resetAccessPoint}
                                    onAddressSaved={this.props.onAddressSaved}
                                />
                            </ShippingAddressInformation>
                        ) : (
                            <ShipAddressSection
                                isNewUserFlow={isNewUserFlow && !addressList.length}
                                shippingAddress={isSddWithoutAddressMatch && isEditViewFromURL ? null : address}
                                profileAddresses={isSddWithoutAddressMatch && isEditViewFromURL ? [] : addressList || []}
                                shippingGroupId={shippingGroupId}
                                isComplete={isComplete}
                                orderHasPhysicalGiftCard={orderHasPhysicalGiftCard}
                                isZeroCheckout={isZeroCheckout}
                                isGuestCheckout={isGuestCheckout}
                                shippingMethod={shippingMethod}
                                setAccessPoint={isSddWithoutAddressMatch ? this.setIsSddWithoutAddressMatch : this.setEditChangeLink}
                                isHalAvailable={isHalAvailable}
                                isSdd={isSdd}
                                isCollapsed={isCollapsed}
                                isEditView={isEditViewFromURL}
                                analyticsCallback={() => {
                                    isSdd && !isEditByChangeLink && this.fireAnalytics();
                                }}
                                showCancelButtonInChild={showCancelButtonInChild}
                                onAddressSaved={this.props.onAddressSaved}
                            >
                                {isHalAvailable && (
                                    <AccessPoint
                                        isHal={isHal}
                                        accessPointCallback={this.setIsEdit}
                                        full
                                    />
                                )}
                            </ShipAddressSection>
                        )}
                    </Box>
                )}
            </>
        );
    };

    render() {
        const {
            orderType,
            localization,
            sectionNumber,
            isSdd,
            isNewUserFlow,
            isCollapsed,
            hasSDUOnlyInSddBasket,
            sectionLevelError,
            hasCmsContent,
            isEditViewFromURL,
            shippingGroup
        } = this.props;
        const { loadingAddress, isEditByChangeLink } = this.state;

        let title;

        if (orderType === GET_IT_SHIPPED) {
            title = localization.getItShippedTitle;
        }

        const isSectionCollapsed = isCollapsed || (!isEditViewFromURL && !shippingGroup?.address);

        const subTitle = isSectionCollapsed ? null : this.getSubtitle();

        return (
            <LayoutCard
                sectionInfo={{
                    sectionNumber: !sectionLevelError && sectionNumber,
                    title,
                    subTitle,
                    hasDivider: true,
                    isChangePermitted: !isEditViewFromURL,
                    onChangeClick: this.setEditChangeLink,
                    sectionLevelError,
                    sectionName: SECTION_NAMES.DELIVER_TO
                }}
                {...(hasCmsContent && { marginTop: [4, 4, 5] })}
                hasPaddingForChildren={false}
                isEditMode={(isEditViewFromURL || isNewUserFlow) && !isCollapsed}
                buttonCustomStyle={styles}
                isNewUserFlow={isNewUserFlow}
                isCollapsed={isSectionCollapsed}
                ariaLabel={localization.deliveryAndPickupInformation}
                role='region'
            >
                {loadingAddress && isSdd ? (
                    <Box
                        paddingX={[4, 4, 5]}
                        minHeight={133}
                    />
                ) : (
                    !isSectionCollapsed && (
                        <>
                            {this.renderCardBody()}
                            {isSdd && !hasSDUOnlyInSddBasket && !isEditViewFromURL && !isEditByChangeLink && <SDDInstructions />}
                        </>
                    )
                )}
            </LayoutCard>
        );
    }
}

const styles = {
    button: {
        width: '48%'
    }
};

export default wrapComponent(DeliverTo, 'DeliverTo', true);
